import { localizeCategoryLabel } from '../../../../utils/category-labels';
import { t as uiT } from '../../../../utils/ui-strings';
import { type SuggestedAction } from '../../../../types/ai';
import {
  CART_ADD_ACTION_PREFIX,
  PRODUCT_FOLLOWUP_ACTION_PREFIX,
  PRODUCT_ID_FROM_ASSET_PATTERN,
  PRODUCT_SELECT_ACTION_PREFIX,
  PRODUCT_SIMILAR_ACTION_PREFIX,
} from '../constants';
import type { ProductActionButton, ProductCardData, SliderProductLike } from '../types';

export function buildFallbackProductActionButton(
  productId?: string,
  slot?: string,
  hasSizes?: boolean,
  lang?: string,
): ProductActionButton | undefined {
  const normalizedProductId = String(productId ?? '').trim();
  if (!normalizedProductId) {
    return undefined;
  }

  if (hasSizes) {
    return {
      label: uiT('add_to_cart', lang),
      value: `${CART_ADD_ACTION_PREFIX}${normalizedProductId}`,
      requiresSize: true,
    };
  }

  const normalizedSlot = String(slot ?? '').trim();
  const label = normalizedSlot
    ? `${uiT('add', lang)} ${localizeCategoryLabel(normalizedSlot, undefined, lang)}`
    : uiT('select', lang);

  return {
    label,
    value: `${PRODUCT_SELECT_ACTION_PREFIX}${normalizedProductId}`,
  };
}

export function extractActionProductId(
  action: SuggestedAction | null | undefined,
  prefix: typeof CART_ADD_ACTION_PREFIX | typeof PRODUCT_SELECT_ACTION_PREFIX,
): string | null {
  const candidates = [action?.actionId, action?.value]
    .map((value) => String(value ?? '').trim())
    .filter(Boolean);

  for (const candidate of candidates) {
    if (!candidate.startsWith(prefix)) continue;
    const rawId = candidate.slice(prefix.length).split('?')[0]?.trim();
    if (rawId) return decodeURIComponent(rawId);
  }

  return null;
}

export function uniqueProductIds(ids: Array<string | null>): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const id of ids) {
    const normalized = String(id ?? '').trim();
    if (!normalized || seen.has(normalized)) continue;
    seen.add(normalized);
    result.push(normalized);
  }
  return result;
}

export function extractProductIdFromCardAssets(
  card: Pick<ProductCardData, 'image' | 'url'>,
): string {
  const imageId = String(card.image ?? '').match(PRODUCT_ID_FROM_ASSET_PATTERN)?.[1];
  if (imageId) return imageId;

  const urlId = String(card.url ?? '').match(PRODUCT_ID_FROM_ASSET_PATTERN)?.[1];
  return urlId ?? '';
}

export function hasProductSizes(card: { sizes?: SliderProductLike['sizes'] }): boolean {
  if (Array.isArray(card.sizes)) return card.sizes.length > 0;
  return String(card.sizes ?? '').trim().length > 0;
}

export function hydrateMissingProductIdsFromActions(
  cards: ProductCardData[],
  suggestedActions: SuggestedAction[] | undefined,
): ProductCardData[] {
  if (cards.length === 0) return cards;

  const actions = Array.isArray(suggestedActions) ? suggestedActions : [];
  const cartIds = uniqueProductIds(
    actions.map((action) => extractActionProductId(action, CART_ADD_ACTION_PREFIX)),
  );
  const selectIds = uniqueProductIds(
    actions.map((action) => extractActionProductId(action, PRODUCT_SELECT_ACTION_PREFIX)),
  ).filter((id) => !cartIds.includes(id));
  const actionIds = [...cartIds, ...selectIds];
  let actionIdIndex = 0;

  return cards.map((card) => {
    const currentId = String(card.id ?? '').trim();
    if (currentId) return card;

    const assetId = extractProductIdFromCardAssets(card);
    const actionId = actionIds[actionIdIndex];
    if (actionId) actionIdIndex += 1;

    const nextId = assetId || actionId;
    return nextId ? { ...card, id: nextId } : card;
  });
}

export function isProductSelectAction(action: SuggestedAction | null | undefined): boolean {
  if (!action) return false;
  return String(action.actionId ?? '').startsWith(PRODUCT_SELECT_ACTION_PREFIX);
}

export function normalizeActionText(value: unknown): string {
  return String(value ?? '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .normalize('NFC')
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function isSingleProductDetailAction(action: SuggestedAction): boolean {
  const actionId = String(action.actionId ?? '');
  const normalizedLabel = normalizeActionText(action.label);
  const normalizedValue = normalizeActionText(action.value);

  if (
    actionId.startsWith(PRODUCT_FOLLOWUP_ACTION_PREFIX) ||
    actionId.startsWith(PRODUCT_SIMILAR_ACTION_PREFIX)
  ) {
    return true;
  }

  return (
    normalizedLabel === 'full info om produkten' ||
    normalizedLabel === 'full product info' ||
    normalizedLabel === 'visa liknande' ||
    normalizedLabel === 'show similar' ||
    normalizedValue === 'beratta mer om produkten' ||
    normalizedValue === 'tell me more about this product' ||
    normalizedValue === 'visa liknande produkter' ||
    normalizedValue === 'show similar products'
  );
}

export function buildProductActionButtons(
  matchedActions: SuggestedAction[],
  slot?: string,
  productId?: string,
  lang?: string,
): ProductActionButton[] {
  return matchedActions
    .map((matchedAction) => {
      const isBrowse = matchedAction.value.startsWith('outfit_select:');
      const isCartAdd = String(matchedAction.actionId ?? matchedAction.value).startsWith(
        CART_ADD_ACTION_PREFIX,
      );
      const isCatalogSelection = isProductSelectAction(matchedAction);
      const catalogActionValue =
        isCatalogSelection && slot && productId
          ? `outfit_select:${slot}:${productId}`
          : matchedAction.value;
      const catalogLabel =
        isCatalogSelection && slot
          ? `${uiT('add', lang)} ${localizeCategoryLabel(slot, undefined, lang)}`
          : uiT('select', lang);

      return {
        label: isBrowse
          ? uiT('select', lang)
          : isCatalogSelection
            ? catalogLabel
            : matchedAction.label,
        value: isCatalogSelection ? catalogActionValue : matchedAction.value,
        requiresSize: isCartAdd,
      };
    })
    .filter(
      (action, actionIndex, list) =>
        list.findIndex(
          (candidate) => candidate.label === action.label && candidate.value === action.value,
        ) === actionIndex,
    );
}
